import { useState, useMemo, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { fuzzyFilter } from "@/lib/fuzzySearch";
import { toast } from "sonner";
import {
  FileText, DollarSign, Check as CheckIcon, Link2 as LinkIcon,
  Copy, ArrowLeft, ArrowRight, X, Search, MessageCircle, ExternalLink,
} from "lucide-react";

const backendUrl = process.env.REACT_APP_BACKEND_URL; // eslint-disable-line no-unused-vars

/**
 * Wizard de 3 pasos para personalizar + compartir el catálogo del vendedor.
 *
 * Se abre desde el panel interno del vendedor (CompartirCatalogoCard o el
 * botón del sidebar). NUNCA debe aparecer en el link público porque el
 * cliente final no debe poder modificar precios/filtros.
 *
 * Pasos:
 *   1. Categorías — buscador + multi-select rápido
 *   2. Precios — toggle on/off + modo (rango|minimo|sugerido)
 *   3. Compartir — Link con settings embebidos (Copiar / WhatsApp / Ver) +
 *      Exportar PDFs (uno por categoría o uno total).
 */
export default function WizardCatalogoDialog({ open, onOpenChange }) {
  const [link, setLink] = useState(null);
  const [categorias, setCategorias] = useState([]);

  const [paso, setPaso] = useState(1);
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [chipSearch, setChipSearch] = useState("");
  const [incluirPrecios, setIncluirPrecios] = useState(false);
  const [precioModo, setPrecioModo] = useState("rango");
  const [unoPorCat, setUnoPorCat] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState({ cur: 0, total: 0 });
  const [linkCopiado, setLinkCopiado] = useState(false);

  // Carga catálogo del vendedor cuando se abre
  useEffect(() => {
    if (!open) return;
    api.get("/me/catalogo-link").then((r) => setLink(r.data)).catch(() => setLink(null));
    api.get("/me/catalogo-categorias").then((r) => {
      const d = r.data;
      const list = Array.isArray(d) ? d : (d?.categorias || []);
      setCategorias(list);
    }).catch(() => setCategorias([]));
    // Reset estado del wizard al abrir
    setPaso(1);
    setSeleccionadas([]);
    setChipSearch("");
    setIncluirPrecios(false);
    setPrecioModo("rango");
    setUnoPorCat(true);
    setExporting(false);
    setProgress({ cur: 0, total: 0 });
    setLinkCopiado(false);
  }, [open]);

  // Filtro fuzzy en vivo del buscador
  const catsFiltradas = useMemo(() => {
    if (!chipSearch.trim()) return categorias;
    return fuzzyFilter(categorias, chipSearch, ["nombre", "id"]);
  }, [categorias, chipSearch]);

  const toggleCat = (id) => {
    setSeleccionadas((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const seleccionarTodasFiltradas = () => {
    const ids = catsFiltradas.map((c) => c.id);
    setSeleccionadas((prev) => Array.from(new Set([...prev, ...ids])));
  };
  const limpiarSeleccion = () => setSeleccionadas([]);

  // URL final con todos los settings embebidos.
  // IMPORTANTE: el link devuelto por /me/catalogo-link ya trae `path` como
  // "/c/<slug>" (que es la ruta pública real en el router). No construir
  // manualmente a /catalogo/<slug> porque esa ruta NO existe y Protected la
  // redirige al login.
  const slug = link?.slug || link?.path?.split("/").filter(Boolean).pop();
  const basePath = link?.path || (slug ? `/c/${slug}` : "");
  const linkPersonalizado = useMemo(() => {
    if (!basePath) return "";
    const url = new URL(`${window.location.origin}${basePath}`);
    if (seleccionadas.length > 0 && seleccionadas.length < categorias.length) {
      url.searchParams.set("cats", seleccionadas.join(","));
    }
    if (incluirPrecios) {
      url.searchParams.set("incluir_precios", "true");
      url.searchParams.set("precio_modo", precioModo);
    }
    return url.toString();
  }, [basePath, seleccionadas, categorias.length, incluirPrecios, precioModo]);

  const copiarLink = useCallback(async () => {
    if (!linkPersonalizado) return;
    try {
      await navigator.clipboard.writeText(linkPersonalizado);
      setLinkCopiado(true);
      toast.success("Link copiado");
      setTimeout(() => setLinkCopiado(false), 2500);
    } catch {
      toast.error("No se pudo copiar. Selecciona el texto manualmente.");
    }
  }, [linkPersonalizado]);

  const nombresSeleccionados = useMemo(() => {
    if (seleccionadas.length === 0) return null;
    return categorias.filter((c) => seleccionadas.includes(c.id)).map((c) => c.nombre).join(", ");
  }, [seleccionadas, categorias]);

  const mensajeWhatsApp = useMemo(() => {
    const txt = nombresSeleccionados
      ? `¡Hola! Te comparto el catálogo de ${nombresSeleccionados} actualizado. Ahí puedes ver lo que tenemos disponible: ${linkPersonalizado}`
      : `¡Hola! Te comparto el catálogo actualizado. Ahí puedes ver todo lo que tenemos disponible: ${linkPersonalizado}`;
    return encodeURIComponent(txt);
  }, [linkPersonalizado, nombresSeleccionados]);

  const exportarPDF = useCallback(async () => {
    if (exporting || !slug) return;
    setExporting(true);
    const tid = toast.loading("Preparando catálogo PDF…");
    try {
      const params = new URLSearchParams({
        orden: "recientes",
        incluir_precios: String(incluirPrecios),
        precio_modo: precioModo,
        uno_por_categoria: String(unoPorCat),
      });
      if (seleccionadas.length > 0) params.set("cats", seleccionadas.join(","));

      // Feb/2026: ESTRATEGIA DE 2 CAMINOS.
      // 1) Intentar PRIMERO el endpoint SÍNCRONO (/exportar-pdf-sync). Devuelve
      //    el ZIP directo en 1 sola llamada HTTP. No hay polling, no hay job_id,
      //    no hay estado en memoria del backend → inmune al error "job expiró"
      //    cuando el backend se reinicia por deploy.
      //    - El backend limita el sync a ≤ 25 páginas. Si es más grande,
      //      devuelve 413 y caemos al flujo async.
      // 2) Fallback: endpoint ASYNC (/exportar-pdf + poll + download) con el
      //    auto-restart on 404 que ya teníamos.
      try {
        toast.loading("Generando catálogo PDF…", { id: tid });
        const res = await api.post(
          `/catalogo/${slug}/exportar-pdf-sync?${params.toString()}`,
          {}, { responseType: "blob", timeout: 90000 },
        );
        // Si el backend devolvió el ZIP OK, descargarlo y terminar.
        const totalCats = parseInt(res.headers?.["x-total-categorias"] || "1", 10) || 1;
        const filename = res.headers?.["x-filename"] || `catalogo-${slug}.zip`;
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 2000);
        toast.success(
          `Catálogo descargado${totalCats > 1 ? ` · ${totalCats} categorías` : ""}`,
          { id: tid, duration: 5000 },
        );
        return;
      } catch (syncErr) {
        const code = syncErr?.response?.status;
        if (code === 404) {
          // No hay productos para los filtros
          toast.error("No hay productos para esos filtros. Ajusta la selección.", { id: tid });
          return;
        }
        // Si NO es un 413 (catálogo demasiado grande para sync), seguimos al async.
        // Si es 413, también seguimos al async (es el caso esperado para grandes).
        // Si es un 5xx, también probamos async por si el sync estaba down pero async no.
        console.info(`sync falló (${code || "red"}), fallback a async`);
      }

      // Feb/2026: función que ejecuta START + POLL + devuelve jobId cuando done.
      // Separada en helper para poder auto-reintentar el ciclo completo si el
      // backend pierde el job a mitad (reinicio por deploy → in-memory dict
      // vaciado → 404 mientras polleamos). Antes el usuario tenía que volver
      // a tocar "Generar PDF" manualmente.
      const runGeneration = async (etiquetaIntento = "") => {
        // --- START con reintento 5xx ---
        let start = null;
        let startError = null;
        for (let i = 1; i <= 3; i += 1) {
          try {
            start = await api.post(
              `/catalogo/${slug}/exportar-pdf?${params.toString()}`,
              {}, { timeout: 60000 },
            );
            break;
          } catch (err) {
            startError = err;
            const code = err?.response?.status || 0;
            if (code !== 0 && (code < 500 || code >= 600)) throw err;
            if (i < 3) {
              toast.loading(`${etiquetaIntento}Red intermitente, reintentando (${i}/3)…`, { id: tid });
              await new Promise((r) => setTimeout(r, 1500 * i));
            }
          }
        }
        if (!start) throw startError || new Error("No se pudo iniciar el PDF.");
        const jobId = start.data.job_id;
        const totalPag = start.data.total_paginas || 0;
        const totalCats = start.data.total_categorias || 1;
        setProgress({ cur: 0, total: totalPag });
        if (!jobId) throw new Error("El servidor no devolvió un job_id válido");

        // --- POLL con tolerancia 5xx ---
        const deadline = Date.now() + 10 * 60 * 1000;
        let status = "processing";
        let fallosConsecutivos = 0;
        const MAX_FALLOS = 5;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 2000));
          try {
            const st = await api.get(`/catalogo-export/${jobId}`, { timeout: 20000 });
            status = st.data.status;
            fallosConsecutivos = 0;
            const p = st.data.progress || 0;
            setProgress({ cur: p, total: totalPag });
            if (status === "done") break;
            if (status === "error") {
              // Feb/2026: si el backend detectó un job stuck (hot reload mató
              // el thread), lanzamos JOB_EXPIRED para que el runner de arriba
              // lo auto-reinicie en vez de mostrarle al vendedor un error raro.
              if (st.data.error === "stuck_restart_needed") {
                const expired = new Error("JOB_STUCK");
                expired.jobExpired = true;
                throw expired;
              }
              throw new Error(st.data.error || "Error generando PDF");
            }
            toast.loading(totalPag ? `${etiquetaIntento}Generando ${p}/${totalPag} páginas…` : `${etiquetaIntento}Generando…`, { id: tid });
          } catch (pollErr) {
            // 404 = job desapareció. El caller decide si auto-reintenta.
            if (pollErr?.response?.status === 404) {
              const expired = new Error("JOB_EXPIRED");
              expired.jobExpired = true;
              throw expired;
            }
            const sttCode = pollErr?.response?.status || 0;
            if (sttCode === 0 || (sttCode >= 500 && sttCode < 600)) {
              fallosConsecutivos += 1;
              if (fallosConsecutivos < MAX_FALLOS) {
                toast.loading(`${etiquetaIntento}Red intermitente, reintentando (${fallosConsecutivos}/${MAX_FALLOS})…`, { id: tid });
                continue;
              }
              throw new Error("Problema de red consultando el estado del PDF. Intenta de nuevo en un momento.");
            }
            throw pollErr;
          }
        }
        if (status !== "done") throw new Error("Tiempo agotado. Intenta con menos productos.");
        return { jobId, totalPag, totalCats, filename: start.data.filename };
      };

      // Feb/2026: hasta 3 intentos del ciclo completo. Si el backend se reinicia
      // (deploy en caliente) o el thread se muere stuck, cada intento falla con
      // jobExpired/jobStuck y re-arrancamos sin pedirle nada al vendedor.
      let gen = null;
      let lastExpiredErr = null;
      for (let intento = 1; intento <= 3; intento += 1) {
        try {
          const etiqueta = intento === 1 ? "" : `Reintento ${intento - 1}/2: `;
          if (intento > 1) {
            toast.loading(`El servidor se reinició, reintentando (${intento - 1}/2)…`, { id: tid });
            await new Promise((r) => setTimeout(r, 2000));
          }
          gen = await runGeneration(etiqueta);
          break;
        } catch (err) {
          if (err?.jobExpired) {
            lastExpiredErr = err;
            continue; // reintentar el ciclo
          }
          throw err; // error de verdad, propagar
        }
      }
      if (!gen) {
        // 3 intentos y seguimos con backend que se reinicia. Mensaje claro.
        throw new Error("El servidor está reiniciándose seguido. Intenta de nuevo en 1-2 minutos.");
      }
      // eslint-disable-next-line prefer-const
      let { jobId, totalCats, filename: zipFilename } = gen;

      let pdfs = [];
      let listError = null;
      try {
        const { data: lst } = await api.get(`/catalogo-export/${jobId}/pdf-list`, { timeout: 15000 });
        pdfs = lst.pdfs || [];
      } catch (listErr) {
        if (listErr?.response?.status === 404) {
          // Backend perdió el job entre done y pdf-list. Re-arrancar generación una vez.
          toast.loading("El servidor liberó el PDF. Rearrancando…", { id: tid });
          try {
            const regen = await runGeneration("Reintento: ");
            const { data: lst2 } = await api.get(`/catalogo-export/${regen.jobId}/pdf-list`, { timeout: 15000 });
            pdfs = lst2.pdfs || [];
            // Actualizar jobId local al nuevo
            gen = regen;
          } catch (regenErr) {
            listError = regenErr;
          }
        } else {
          listError = listErr;
        }
      }
      if (listError) throw listError;
      if (pdfs.length === 0) throw new Error("No se generó ningún PDF. Revisa los filtros.");
      const effectiveJobId = gen.jobId;
      const effectiveZipFilename = gen.filename || zipFilename;

      // Feb/2026: cuando hay >5 PDFs (ej. 395 categorías), el navegador bloquea
      // las descargas múltiples después de ~10. Por eso descargamos el ZIP
      // completo en un solo click — el usuario lo descomprime y manda cada PDF.
      // Para 1-5 PDFs se mantiene el flujo original (más conveniente para
      // compartir inmediato por WhatsApp sin descomprimir).
      const BULK_THRESHOLD = 5;
      if (pdfs.length > BULK_THRESHOLD) {
        toast.loading(`Comprimiendo ${pdfs.length} PDFs en un ZIP…`, { id: tid });
        try {
          const res = await api.get(
            `/catalogo-export/${effectiveJobId}/download`,
            { responseType: "blob", timeout: 180000 },
          );
          const url = window.URL.createObjectURL(res.data);
          const a = document.createElement("a");
          a.href = url;
          a.download = effectiveZipFilename || `catalogo-${effectiveJobId}.zip`;
          document.body.appendChild(a); a.click(); a.remove();
          setTimeout(() => window.URL.revokeObjectURL(url), 2000);
          toast.success(
            `ZIP con ${pdfs.length} PDF${pdfs.length === 1 ? "" : "s"} descargado. Descomprímelo y envía cada archivo por WhatsApp.`,
            { id: tid, duration: 7000 },
          );
          return; // listo
        } catch (zipErr) {
          if (zipErr?.response?.status === 404) {
            throw new Error("El ZIP expiró antes de descargarse. Reintenta.");
          }
          throw zipErr;
        }
      }

      toast.loading(`Descargando ${pdfs.length} PDF${pdfs.length === 1 ? "" : "s"}…`, { id: tid });
      let descargados = 0;
      for (const nombre of pdfs) {
        try {
          const res = await api.get(
            `/catalogo-export/${effectiveJobId}/pdf/${encodeURIComponent(nombre)}`,
            { responseType: "blob", timeout: 60000 },
          );
          const url = window.URL.createObjectURL(res.data);
          const a = document.createElement("a");
          a.href = url; a.download = nombre;
          document.body.appendChild(a); a.click(); a.remove();
          setTimeout(() => window.URL.revokeObjectURL(url), 2000);
          descargados += 1;
          await new Promise((r) => setTimeout(r, 400));
        } catch (err) {
          console.warn(`No se pudo descargar ${nombre}`, err);
        }
      }
      api.post(`/catalogo-export/${effectiveJobId}/liberar`).catch(() => {});
      if (descargados === 0) {
        throw new Error("No se pudo descargar ningún PDF. Reintenta.");
      }
      toast.success(
        unoPorCat
          ? `${descargados} PDF${descargados === 1 ? "" : "s"} descargado${descargados === 1 ? "" : "s"} · ${totalCats} categoría${totalCats === 1 ? "" : "s"}`
          : "PDF descargado",
        { id: tid },
      );
    } catch (e) {
      // Feb/2026: NUNCA mostrar strings técnicos crudos (JOB_EXPIRED, JOB_STUCK,
      // HTML de Cloudflare, stack traces). Mapear a mensajes amables.
      const code = e?.response?.status;
      let detalle = e?.response?.data?.detail;
      if (typeof detalle !== "string" || detalle.length > 300) detalle = null;
      // Lista negra de strings técnicos que nunca deben llegar al vendedor.
      const BLACKLIST = ["JOB_EXPIRED", "JOB_STUCK", "stuck_restart_needed", "jobExpired"];
      const isTechnical = (s) => !s || BLACKLIST.some((k) => s.includes(k)) || s.includes("<");
      let msg;
      if (code === 404) {
        msg = "No hay productos para esos filtros. Ajusta la selección.";
      } else if (code && code >= 500 && code < 600) {
        msg = "El servidor tardó en responder. Reintenta en un momento.";
      } else if (detalle && !isTechnical(detalle)) {
        msg = detalle;
      } else if (typeof e?.message === "string" && e.message.length < 300 && !isTechnical(e.message)) {
        msg = e.message;
      } else {
        msg = "No se pudo generar el PDF. Reintenta en un momento.";
      }
      toast.error(msg, { id: tid });
    } finally {
      setExporting(false);
    }
  }, [exporting, slug, seleccionadas, incluirPrecios, precioModo, unoPorCat]);

  if (!open) return null;

  const totalPasos = 3;
  const puedeAvanzar = paso === 1 ? true : true; // seleccionadas vacías = todas (default)
  const pct = progress.total > 0 ? Math.round((progress.cur / progress.total) * 100) : 0;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto"
      onClick={(e) => {
        // Oct/2026: NO cerrar al tocar el backdrop — demasiados accidentes
        // (abrir el link en nueva pestaña y perder el wizard). Solo cerramos
        // vía el botón X. Si están exportando, bloqueamos cualquier close.
        e.stopPropagation();
      }}
    >
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl flex flex-col max-h-[calc(100vh-2rem)] my-auto" onClick={(e) => e.stopPropagation()} data-testid="wizard-catalogo-modal">
        {/* Header + stepper */}
        <div className="p-5 border-b border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase text-primary tracking-widest">Compartir con un cliente</div>
                <div className="text-lg font-bold text-slate-800">Personalizar catálogo</div>
              </div>
            </div>
            <button onClick={() => onOpenChange?.(false)} disabled={exporting} className="text-slate-400 hover:text-slate-600 disabled:opacity-30" data-testid="wizard-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {[
              { n: 1, l: "Categorías" },
              { n: 2, l: "Precios" },
              { n: 3, l: "Compartir" },
            ].map((s, idx) => {
              const activo = paso === s.n;
              const done = paso > s.n;
              return (
                <div key={s.n} className="flex items-center gap-2 flex-1">
                  <div className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border-2 ${activo ? "border-primary bg-primary/5" : done ? "border-emerald-300 bg-emerald-50" : "border-slate-200"}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${activo ? "bg-primary text-white" : done ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                      {done ? <CheckIcon className="w-3.5 h-3.5" /> : s.n}
                    </div>
                    <span className={`text-xs font-semibold ${activo ? "text-primary" : done ? "text-emerald-700" : "text-slate-500"}`}>{s.l}</span>
                  </div>
                  {idx < 2 && <div className={`w-4 h-0.5 ${done ? "bg-emerald-300" : "bg-slate-200"}`} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {paso === 1 && (
            <section className="space-y-3" data-testid="wizard-paso-1">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">
                  Elige las categorías
                  <span className="ml-2 text-primary tabular-nums">{seleccionadas.length}/{categorias.length}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <button onClick={seleccionarTodasFiltradas} className="text-primary hover:underline font-semibold" data-testid="wizard-cats-all">
                    {chipSearch.trim() ? "Agregar visibles" : "Todas"}
                  </button>
                  <span className="text-slate-300">·</span>
                  <button onClick={limpiarSeleccion} className="text-slate-500 hover:text-slate-700 font-semibold" data-testid="wizard-cats-clear">Limpiar</button>
                </div>
              </div>
              {/* Buscador */}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={chipSearch}
                  onChange={(e) => setChipSearch(e.target.value)}
                  placeholder={`Buscar entre ${categorias.length} categorías…`}
                  className="w-full h-10 pl-9 pr-3 border-2 border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary/50"
                  data-testid="wizard-cats-search"
                  autoFocus
                />
              </div>
              {/* Lista con checkboxes - solo las que matchean el filtro */}
              <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="wizard-cats-list">
                {catsFiltradas.length === 0 ? (
                  <div className="text-xs text-slate-400 italic px-3 py-6 text-center">
                    No hay categorías que coincidan con "{chipSearch}"
                  </div>
                ) : catsFiltradas.map((c) => {
                  const active = seleccionadas.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCat(c.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50 transition-colors ${active ? "bg-primary/5" : ""}`}
                      data-testid={`wizard-cat-${String(c.id).substring(0, 32)}`}
                    >
                      <span className={`w-4 h-4 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors ${active ? "bg-primary border-primary" : "border-slate-300 bg-white"}`}>
                        {active && <CheckIcon className="w-3 h-3 text-white" />}
                      </span>
                      <span className={`flex-1 truncate ${active ? "font-semibold text-primary" : "text-slate-700"}`}>{c.nombre}</span>
                      {c.count > 0 && (
                        <span className={`text-[10px] rounded-full px-1.5 py-0.5 flex-shrink-0 ${active ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>
                          {c.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500">
                Si no eliges ninguna, el link mostrará todo el catálogo.
              </p>
            </section>
          )}

          {paso === 2 && (
            <section className="space-y-3" data-testid="wizard-paso-2">
              <div className="text-sm font-semibold text-slate-800">¿Mostramos precios al cliente?</div>
              <label className="flex items-center gap-3 px-4 py-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50" data-testid="wizard-toggle-precios">
                <input type="checkbox" checked={incluirPrecios} onChange={(e) => setIncluirPrecios(e.target.checked)}
                       className="w-4 h-4 accent-primary flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-800">Mostrar precios</div>
                  <div className="text-xs text-slate-500">Apagado = vitrina sin valores. Encendido = elige abajo cómo verlo.</div>
                </div>
                <DollarSign className={`w-5 h-5 ${incluirPrecios ? "text-primary" : "text-slate-300"}`} />
              </label>
              {incluirPrecios && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="wizard-precio-modos">
                  {[
                    { v: "rango", label: "Rango completo", desc: "mín — sug." },
                    { v: "minimo", label: "Solo mínimo", desc: "piso al cliente" },
                    { v: "sugerido", label: "Solo sugerido", desc: "precio público" },
                  ].map((opt) => {
                    const sel = precioModo === opt.v;
                    return (
                      <button key={opt.v} type="button" onClick={() => setPrecioModo(opt.v)}
                        className={`text-left px-3 py-2.5 border-2 rounded-lg transition ${sel ? "border-primary bg-primary/5" : "border-slate-200 hover:bg-slate-50"}`}
                        data-testid={`wizard-precio-${opt.v}`}
                      >
                        <div className="flex items-center gap-1.5">
                          {sel && <CheckIcon className="w-4 h-4 text-primary" />}
                          <div className="text-sm font-semibold text-slate-800">{opt.label}</div>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {paso === 3 && (
            <section className="space-y-4" data-testid="wizard-paso-3">
              <div className="text-sm font-semibold text-slate-800">¿Cómo lo compartes?</div>

              {/* Opción A: Link */}
              <div className="border border-slate-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                    <LinkIcon className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-slate-800">Opción A · Compartir link personalizado</div>
                    <div className="text-[11px] text-slate-500">El cliente lo abre en su celular con los settings elegidos</div>
                  </div>
                </div>
                <div className="pl-10 space-y-2">
                  <div className="flex items-center gap-2">
                    <input readOnly value={linkPersonalizado}
                           className="flex-1 h-9 px-3 border border-slate-200 rounded-lg text-[11px] font-mono bg-slate-50 text-slate-700 truncate"
                           data-testid="wizard-link-input" />
                    <button onClick={copiarLink}
                            className={`h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 whitespace-nowrap ${linkCopiado ? "bg-emerald-600 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
                            data-testid="wizard-link-copiar">
                      {linkCopiado ? <><CheckIcon className="w-3.5 h-3.5" /> Copiado</> : <><Copy className="w-3.5 h-3.5" /> Copiar</>}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`https://wa.me/?text=${mensajeWhatsApp}`} target="_blank" rel="noopener noreferrer"
                       className="h-8 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold flex items-center gap-1.5"
                       data-testid="wizard-link-whatsapp">
                      <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                    <a href={linkPersonalizado} target="_blank" rel="noopener noreferrer"
                       className="h-8 px-3 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-[11px] font-semibold flex items-center gap-1.5"
                       data-testid="wizard-link-abrir">
                      <ExternalLink className="w-3.5 h-3.5" /> Ver cómo se ve
                    </a>
                  </div>
                </div>
              </div>

              {/* Opción B: PDF */}
              <div className="border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-slate-800">Opción B · Exportar PDF</div>
                    <div className="text-[11px] text-slate-500">Archivos descargables para enviar por WhatsApp/email</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-10">
                  <button type="button" onClick={() => setUnoPorCat(true)}
                    className={`text-left px-3 py-2.5 border-2 rounded-lg transition ${unoPorCat ? "border-primary bg-primary/5" : "border-slate-200 hover:bg-slate-50"}`}
                    data-testid="wizard-formato-por-cat"
                  >
                    <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                      {unoPorCat && <CheckIcon className="w-3.5 h-3.5 text-primary" />}
                      Un PDF por categoría
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {(() => {
                        const catsCount = seleccionadas.length || categorias.length;
                        if (catsCount > 5) return `Se baja 1 ZIP con ${catsCount} PDFs adentro`;
                        return "Archivos separados";
                      })()}
                    </div>
                  </button>
                  <button type="button" onClick={() => setUnoPorCat(false)}
                    className={`text-left px-3 py-2.5 border-2 rounded-lg transition ${!unoPorCat ? "border-primary bg-primary/5" : "border-slate-200 hover:bg-slate-50"}`}
                    data-testid="wizard-formato-uno"
                  >
                    <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                      {!unoPorCat && <CheckIcon className="w-3.5 h-3.5 text-primary" />}
                      Un solo PDF
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Todas las categorías unidas</div>
                  </button>
                </div>
                <div className="pl-10">
                  <button onClick={exportarPDF} disabled={exporting}
                    className="h-10 px-4 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-bold flex items-center gap-2 disabled:opacity-50"
                    data-testid="wizard-pdf-generar">
                    <FileText className="w-4 h-4" />
                    {exporting ? "Generando…" : "Generar PDF"}
                  </button>
                </div>
                {exporting && progress.total > 0 && (
                  <div className="pl-10">
                    <div className="flex items-center justify-between text-xs text-slate-700 mb-1.5">
                      <span className="font-semibold">Generando…</span>
                      <span className="tabular-nums">{progress.cur} / {progress.total} · {pct}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Resumen */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-[11px] text-slate-600 space-y-0.5">
                <div className="flex items-center gap-1.5"><CheckIcon className="w-3 h-3 text-emerald-600" /> {seleccionadas.length === 0 ? "Todas las categorías" : `${seleccionadas.length} categoría${seleccionadas.length === 1 ? "" : "s"} seleccionada${seleccionadas.length === 1 ? "" : "s"}`}</div>
                <div className="flex items-center gap-1.5"><CheckIcon className="w-3 h-3 text-emerald-600" /> {incluirPrecios ? `Precios ON · modo "${precioModo}"` : "Precios OFF (vitrina sin valores)"}</div>
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 p-4 border-t border-slate-200 bg-slate-50/50">
          <button
            onClick={() => paso === 1 ? onOpenChange?.(false) : setPaso(paso - 1)}
            disabled={exporting}
            className="h-10 px-4 rounded-lg text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="wizard-prev"
          >
            {paso === 1 ? "Cancelar" : <><ArrowLeft className="w-4 h-4" /> Atrás</>}
          </button>
          {paso < totalPasos ? (
            <button
              onClick={() => setPaso(paso + 1)}
              disabled={!puedeAvanzar}
              className="h-10 px-5 rounded-lg text-sm font-bold text-white bg-primary hover:bg-primary/90 disabled:opacity-40 flex items-center gap-1.5"
              data-testid="wizard-next"
            >
              Siguiente <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={() => onOpenChange?.(false)} disabled={exporting}
                    className="h-10 px-5 rounded-lg text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-50"
                    data-testid="wizard-finalizar">
              Cerrar
            </button>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
